import RandExp from 'randexp';
import { resolveSchemaFieldType } from '../dsv-schema-utils';

const FORMAT_SAMPLES = {
  date: '2026-01-01',
  'date-time': '2026-01-01T00:00:00Z',
  time: '00:00:00',
  duration: 'P1D',
  email: 'user@example.com',
  'idn-email': 'user@example.com',
  uri: 'https://example.com',
  url: 'https://example.com',
  'uri-reference': '/path',
  iri: 'https://example.com',
  uuid: '00000000-0000-4000-8000-000000000000',
  hostname: 'example.com',
  ipv4: '192.0.2.1',
  ipv6: '2001:db8::1',
  'json-pointer': '/a/b',
  regex: '.*',
};

function hasOwn(obj, key) {
  return Object.prototype.hasOwnProperty.call(obj, key);
}

/** Deterministic regex→string: always pick the low bound, cap unbounded repeats. */
function generateFromPattern(pattern) {
  try {
    const rx = new RandExp(pattern);
    rx.randInt = (from) => from;
    rx.max = 5;
    return rx.gen();
  } catch {
    return null;
  }
}

function clampString(str, minLength, maxLength) {
  let out = str;
  if (typeof minLength === 'number' && out.length < minLength) {
    out = out.padEnd(minLength, 'x');
  }
  if (typeof maxLength === 'number' && out.length > maxLength) {
    out = out.slice(0, maxLength);
  }
  return out;
}

function generateString(schema) {
  const { pattern, format, minLength, maxLength } = schema;
  if (pattern) {
    const fromPattern = generateFromPattern(pattern);
    if (fromPattern != null) return clampString(fromPattern, minLength, maxLength);
  }
  if (format && hasOwn(FORMAT_SAMPLES, format)) {
    return clampString(FORMAT_SAMPLES[format], undefined, maxLength);
  }
  return clampString('string', minLength, maxLength);
}

function roundToMultiple(value, multipleOf) {
  const k = Math.ceil(value / multipleOf - 1e-9);
  // Use a scaled round to avoid float drift (e.g. multipleOf 0.1).
  const scaled = Math.round(k * multipleOf * 1e9) / 1e9;
  return scaled;
}

function generateNumber(schema, isInteger) {
  const { minimum, maximum, exclusiveMinimum, exclusiveMaximum, multipleOf } = schema;
  // Step away from an exclusive bound by 1 — valid for both integers and
  // numbers (a float bound ± 1 still satisfies the exclusive constraint).
  const step = 1;
  let value = 0;
  if (typeof minimum === 'number') value = minimum;
  else if (typeof exclusiveMinimum === 'number') value = exclusiveMinimum + step;

  if (typeof exclusiveMaximum === 'number' && value >= exclusiveMaximum) value = exclusiveMaximum - step;
  if (typeof maximum === 'number' && value > maximum) value = maximum;

  if (typeof multipleOf === 'number' && multipleOf > 0) {
    value = roundToMultiple(value, multipleOf);
    if (typeof maximum === 'number' && value > maximum) {
      value = Math.floor(maximum / multipleOf) * multipleOf;
    }
  }
  return isInteger ? Math.round(value) : value;
}

/**
 * Generate a single constraint-valid scalar value from a leaf subschema.
 * Precedence: examples > example > const > enum > default > type-specific.
 *
 * @param {object} schema - a JSON Schema leaf subschema
 * @returns {*} a value intended to satisfy the leaf's constraints
 */
export function generateLeaf(schema) {
  if (!schema || typeof schema !== 'object') return 'string';

  if (Array.isArray(schema.examples) && schema.examples.length > 0) return schema.examples[0];
  if (hasOwn(schema, 'example')) return schema.example;
  if (hasOwn(schema, 'const')) return schema.const;
  if (Array.isArray(schema.enum) && schema.enum.length > 0) return schema.enum[0];
  if (hasOwn(schema, 'default')) return schema.default;

  switch (resolveSchemaFieldType(schema)) {
    case 'string': return generateString(schema);
    case 'integer': return generateNumber(schema, true);
    case 'number': return generateNumber(schema, false);
    case 'boolean': return false;
    case 'null': return null;
    default: return 'string';
  }
}
